import {
  Component,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  OnChanges,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Injector,
  OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { ViewEncapsulation, ElementRef, Input } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators, FormArray, AbstractControl, FormControl } from '@angular/forms';

import { BehaviorSubject, of, toArray, Subscription } from 'rxjs';
import { merge, Observable, of as observableOf, pipe } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { finalize } from 'rxjs/operators';

import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { AuthQuery } from '@app/auth/auth.query';
import { JournalService, Journal, MailService, Mail } from '@core';
import { environment } from '../../environments/environment';
import { Logger } from '@core';

import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { KundeService, Kunde, KundeResponse } from '../services/kunde.service';
import { KontaktService } from '../services/kontakt.service';
import { DataChangeClientService } from './changes-client.service';
import { ClientStatusService, ClientStatus } from '../services/client-status.service';
import { TableExportService, ExportColumn } from '../services/table-export.service';
import { PaginationService } from '../core/services/pagination.service';
import { LocalStorageFilterService } from '../core/services/local-storage-filter.service';

declare var $: any;

export interface EmpFilter {
  name: string;
  options: string[];
  defaultValue: string;
  isOpen?: boolean;
}

export interface filterOption {
  name: string;
  value: string;
  isdefault: boolean;
}

@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
})
export class ClientComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'id',
    'suchbegriff',
    'status_id',
    'startdatum',
    'enddatum',
    'actions',
  ];

  dataSource: MatTableDataSource<Kunde>;
  allClients: Kunde[] = []; // Store all clients for filtering
  isLoadingResults = true;
  isRateLimitReached = false;
  resultsLength = 0;

  pageSizes: number[];
  pageSize: number;
  pageIndex = 0;
  sortBy: string = 'suchbegriff';
  sortOrder: 'asc' | 'desc' = 'asc';
  searchValue: string;

  stati: string[] = [];
  selectedStatusFilter: string = 'Aktiv';
  selectedAlphabetFilter: string | null = null;
  //niederlassungen: string[] = ['Wien', 'Graz', 'Salzburg', 'Innsbruck', 'Linz'];
  
  empFilters: EmpFilter[] = [];
  defaultValue = 'All';
  filterDictionary = new Map<string, string>();
  filters$ = new BehaviorSubject<string>('');
  searchTerm$ = new BehaviorSubject<string>('');
  private searchTimeout: any;

  private subscription: Subscription;

  // Data management
  dataSubject = new BehaviorSubject<Kunde[]>([]);
  clients$: Observable<Kunde[]>;
  totalItems: number;

  // LocalStorage keys
  private readonly STORAGE_KEY_SEARCH = 'kunde_search_history';
  private readonly STORAGE_KEY_FILTERS = 'kunde_filters';
  private readonly STORAGE_KEY_PAGINATION = 'kunde_pagination';
  searchHistory: string[] = [];
  showSearchHistory = false;

  constructor(
    private kundeService: KundeService,
    private kontaktService: KontaktService,
    private clientStatusService: ClientStatusService,
    private cfr: ComponentFactoryResolver,
    private _snackBar: MatSnackBar,
    private _fb: UntypedFormBuilder,
    private journalService: JournalService,
    public dialog: MatDialog,
    public dataChangeClientService: DataChangeClientService,
    private injector: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private authQuery: AuthQuery,
    private tableExportService: TableExportService,
    private paginationService: PaginationService,
    private localStorageService: LocalStorageFilterService
  ) {
    console.log('ClientComponent: Constructor called');
    console.log('ClientComponent: KundeService available:', !!this.kundeService);
    
    // Initialize pagination from service
    this.pageSizes = this.paginationService.getPageSizeOptions();
    this.pageSize = this.paginationService.getPageSize();
    
    // Initialize dataSource with empty array
    this.dataSource = new MatTableDataSource<Kunde>([]);
    console.log('ClientComponent: dataSource initialized');
  }

  ngOnInit() {
    console.log('ClientComponent: ngOnInit called');
    
    // Check authentication status
    console.log('ClientComponent: User authenticated:', this.authQuery.isLoggedIn);
    console.log('ClientComponent: Access token available:', !!this.authQuery.access_token);
    console.log('ClientComponent: Access token value:', this.authQuery.access_token);
    
    // Load from localStorage first
    this.loadFromLocalStorage();
    
    // Initialize status options from environment
    this.initializeStatusOptions();
    
    // Initialize filters - use loaded selectedStatusFilter or default to 'Aktiv'
    this.empFilters.push({ 
      name: 'Status', 
      options: ['Alle', ...this.stati], 
      defaultValue: this.selectedStatusFilter || 'Aktiv',  // Use loaded value, fallback to 'Aktiv'
      isOpen: false 
    });
    //this.empFilters.push({ name: 'Niederlassung', options: ['All', ...this.niederlassungen], defaultValue: this.defaultValue, isOpen: false });

    console.log('ClientComponent: Filters initialized:', this.empFilters);

    // Add click outside listener to close dropdowns
    document.addEventListener('click', this.onDocumentClick.bind(this));

    // Subscribe to the sidebar changes
    this.subscription = this.dataChangeClientService.dataChanges$.subscribe((data) => {
      if (data) {
        console.log('Client change received:', data);
        const mappedData = {
          id: data.id,
          suchbegriff: data.suchbegriff,
          niederlassung_code: data.niederlassung_code,
          status_id: data.status_id,
          startdatum: data.startdatum,
          enddatum: data.enddatum,
        };
        this.updateClient(mappedData);
      }
    });

    // Data loading will be handled in ngAfterViewInit
    console.log('ClientComponent: Data loading will be handled in ngAfterViewInit');

    console.log('ClientComponent: ngOnInit completed');
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    // Remove click outside listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  onDocumentClick(event: Event) {
    // Close all dropdowns when clicking outside
    this.empFilters.forEach(filter => {
      filter.isOpen = false;
    });
  }

  toggleDropdown(filter: EmpFilter, event: Event) {
    event.stopPropagation(); // Prevent the document click from firing immediately
    filter.isOpen = !filter.isOpen;
  }

  /**
   * Update the datasource with the given data from the subscription
   */
  updateClient(updatedClient: any) {
    const data = this.dataSubject.getValue();
    const index = data.findIndex((client) => client.id === updatedClient.id);
    if (index > -1) {
      data[index] = updatedClient;
      this.dataSubject.next(data);
      this.dataSource.data = data;
    }
  }

    ngAfterViewInit() {
    console.log('ClientComponent: ngAfterViewInit called');
    
    // Simple data loading without complex merge observables
    this.loadInitialData();
    
    console.log('ClientComponent: ngAfterViewInit completed');
  }

  loadInitialData() {
    console.log('Loading initial client data...');
    this.isLoadingResults = true;
    
    // Get current search term
    const searchTerm = this.searchValue || '';
    console.log('Loading data with search term:', searchTerm);
    
    // Ensure paginator is hooked
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    this.loadPage();
  }

  getTableData$(pageNumber: number, pageSize: number, sort: string, order, searchTerm: string, filtersArgs: string) {
    console.log('ClientComponent: getTableData$ called with params:', {
      pageNumber, pageSize, sort, order, searchTerm, filtersArgs
    });
    const result = this.kundeService.getKunden(pageNumber, pageSize, searchTerm, sort, order);
    console.log('ClientComponent: getTableData$ returning observable:', result);
    return result;
  }

  /**
   * Load search history and filters from localStorage
   */
  loadFromLocalStorage(): void {
    // Load search history
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);

    // Load filters
    const savedFilters = this.localStorageService.loadFilters(this.STORAGE_KEY_FILTERS);
    if (savedFilters) {
      if (savedFilters.selectedStatusFilter) {
        this.selectedStatusFilter = savedFilters.selectedStatusFilter;
      }
      if (savedFilters.selectedAlphabetFilter) {
        this.selectedAlphabetFilter = savedFilters.selectedAlphabetFilter;
      }
    }

    // Load pagination
    const savedPagination = this.localStorageService.loadPagination(this.STORAGE_KEY_PAGINATION);
    if (savedPagination) {
      this.pageIndex = savedPagination.currentPage - 1;
      this.pageSize = savedPagination.pageSize || this.pageSize;
    }

    // Load search value
    if (savedFilters?.searchValue) {
      this.searchValue = savedFilters.searchValue;
      this.searchTerm$.next(this.searchValue);
    }
  }

  /**
   * Save current filters to localStorage
   */
  saveFiltersToLocalStorage(): void {
    const filters = {
      selectedStatusFilter: this.selectedStatusFilter,
      searchValue: this.searchValue || '',
      selectedAlphabetFilter: this.selectedAlphabetFilter
    };
    this.localStorageService.saveFilters(this.STORAGE_KEY_FILTERS, filters);
  }

  /**
   * Save pagination state to localStorage
   */
  savePaginationToLocalStorage(): void {
    const currentPage = this.pageIndex + 1;
    this.localStorageService.savePagination(this.STORAGE_KEY_PAGINATION, currentPage, this.pageSize);
  }

  onSearchInput(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.searchTerm$.next(filterValue);

    // If a text search is active, reset letter filter to "Alle" so results are not hidden
    if (filterValue && filterValue.trim() && this.selectedAlphabetFilter !== null) {
      this.selectedAlphabetFilter = null;
    }
    
    // Hide search history dropdown when typing
    this.showSearchHistory = false;
    
    // Save filters
    this.saveFiltersToLocalStorage();
    
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Set new timeout for debounced search
    this.searchTimeout = setTimeout(() => {
      // Save to search history only when search is executed (after debounce)
      if (this.searchValue && this.searchValue.trim()) {
        this.localStorageService.saveSearchToHistory(this.STORAGE_KEY_SEARCH, this.searchValue);
        this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
      }
      this.loadDataWithSearch();
    }, 300); // 300ms delay
  }

  /**
   * Show search history dropdown on focus
   */
  onSearchFocus(): void {
    // Reload search history from localStorage
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
    if (this.searchHistory.length > 0) {
      this.showSearchHistory = true;
    }
  }

  /**
   * Hide search history dropdown on blur (with delay to allow click)
   */
  onSearchBlur(): void {
    setTimeout(() => {
      this.showSearchHistory = false;
    }, 200);
  }

  /**
   * Select a search term from history
   */
  selectSearchHistory(term: string): void {
    this.searchValue = term;
    this.searchTerm$.next(term);

    // If a text search is active, reset letter filter to "Alle" so results are not hidden
    if (term && term.trim() && this.selectedAlphabetFilter !== null) {
      this.selectedAlphabetFilter = null;
    }

    this.showSearchHistory = false;
    
    // Save to search history (moves to top)
    this.localStorageService.saveSearchToHistory(this.STORAGE_KEY_SEARCH, term);
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
    
    // Save to filters localStorage to persist the selected search value
    this.saveFiltersToLocalStorage();
    
    // Trigger the search query
    this.loadDataWithSearch();
  }

  clearSearchHistory(): void {
    this.localStorageService.clearSearchHistory(this.STORAGE_KEY_SEARCH);
    this.searchHistory = [];
    this.showSearchHistory = false;
    
    // Also clear the searchValue from filters localStorage
    // This prevents the filter from being preselected on next navigation
    const savedFilters = this.localStorageService.loadFilters(this.STORAGE_KEY_FILTERS);
    if (savedFilters) {
      savedFilters.searchValue = '';
      this.localStorageService.saveFilters(this.STORAGE_KEY_FILTERS, savedFilters);
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.searchTerm$.next(filterValue);
    
    // Trigger new data load with search term
    this.loadDataWithSearch();
  }

  clearSearch() {
    this.searchValue = '';
    this.searchTerm$.next('');
    
    // Save to filters localStorage to persist the cleared state
    this.saveFiltersToLocalStorage();
    
    // Trigger new data load without search term
    this.loadDataWithSearch();
  }

  /**
   * Handle alphabet filter click
   */
  onAlphabetFilterClick(letter: string | null): void {
    // If null is passed (Alle button), clear filter
    // Otherwise, toggle: if same letter clicked, clear filter; otherwise set new filter
    if (letter === null) {
      this.selectedAlphabetFilter = null;
    } else if (this.selectedAlphabetFilter === letter) {
      this.selectedAlphabetFilter = null;
    } else {
      this.selectedAlphabetFilter = letter;
    }
    
    // Reset to first page
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    
    // Save to localStorage
    this.saveFiltersToLocalStorage();
    
    // Reload data
    this.loadPage();
  }

  loadDataWithSearch() {
    console.log('Loading data with search term:', this.searchValue);
    this.isLoadingResults = true;
    // Reset to first page on new search
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadPage();
  }

  applyEmpFilter(ob: any, empfilter: EmpFilter) {
    // Handle both MatSelectChange and custom filter objects
    const selectedValue = ob.value || ob;
    
    // Update the filter's default value to show the selected value
    empfilter.defaultValue = selectedValue;
    
    // Store in filter dictionary
    this.filterDictionary.set(empfilter.name, selectedValue);
    
    // Handle status filtering
    if (empfilter.name === 'Status') {
      this.selectedStatusFilter = selectedValue;
      // Reset to first page when filter changes
      this.pageIndex = 0;
      this.saveFiltersToLocalStorage();
      this.savePaginationToLocalStorage();
      if (this.paginator) {
        this.paginator.firstPage();
      }
      // Reload data from API with new filter
      this.loadPage();
    }
    
    // Trigger database query via filters$ observable
    var jsonString = JSON.stringify(Array.from(this.filterDictionary.entries()));
    this.filters$.next(jsonString);
    
    console.log('Filter applied:', empfilter.name, '=', selectedValue);
  }

  showPageDetail(client_uuid: string) {
    console.log('showPageDetail called with client_uuid:', client_uuid, 'type:', typeof client_uuid);

    // Validate client_uuid
    if (!client_uuid || client_uuid === '0' || client_uuid === 'new') {
      console.log('Invalid client_uuid, navigating to new client form');
      console.log('client_uuid value:', client_uuid);
      console.log('client_uuid type:', typeof client_uuid);
      
      // Debug: Log the current data source to see what's available
      console.log('Current dataSource data:', this.dataSource.data);
      console.log('First client in data:', this.dataSource.data[0]);
      
      this.router.navigate(['/client-edit/new']);
    } else {
      console.log('Navigating to edit client with UUID:', client_uuid);
      this.router.navigate(['/client-edit', client_uuid]);
    }
  }

  onStatusChange(event: MatSelectChange, client: Kunde) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '250px',
      data: {
        title: 'Confirm Status Change',
        message: `Are you sure you want to change the status of ${client.suchbegriff} to ${event.value}?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        client.status_id = parseInt(event.value);
        this.kundeService.updateKunde(client.id, client).subscribe(
          (response) => {
            this._snackBar.open('Client status updated successfully', 'Close', {
              duration: 3000,
            });
          },
          (error) => {
            this._snackBar.open('Error updating client status', 'Close', {
              duration: 3000,
            });
          }
        );
      }
    });
  }





  testAuthentication() {
    console.log('=== AUTHENTICATION TEST ===');
    console.log('User authenticated:', this.authQuery.isLoggedIn);
    console.log('Access token available:', !!this.authQuery.access_token);
    console.log('Access token value:', this.authQuery.access_token);
    console.log('User name:', this.authQuery.name);
    console.log('User role:', this.authQuery.role);
    
    // Test API call with current authentication
    console.log('Testing API call...');
    this.kundeService.getKunden(1, 5, '', 'suchbegriff', 'asc').subscribe(
      (response) => {
        console.log('✅ API call successful:', response);
        this._snackBar.open('Authentication successful! API call worked.', 'Close', {
          duration: 3000,
        });
      },
      (error) => {
        console.error('❌ API call failed:', error);
        this._snackBar.open(`Authentication failed: ${error.status} ${error.statusText}`, 'Close', {
          duration: 5000,
        });
      }
    );
  }

  /**
   * Get CSS classes for status badge based on status object
   */
  getStatusClasses(statusId: number): string {
    if (!statusId) {
      return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
    switch (statusId) {
      case 1: return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      case 2: return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3: return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 4: return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      default: return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

  /**
   * Initialize status options from environment
   */
  initializeStatusOptions() {
    const statusArray = (environment as any).client_status || [];
    this.stati = statusArray.map((status: any) => status.label);
    console.log('Status options initialized from environment:', this.stati);
  }

  /**
   * Apply status filter to the data source
   */
  applyStatusFilter() {
    if (this.selectedStatusFilter === 'Alle') {
      // Show all clients
      this.dataSource.data = this.allClients || [];
    } else {
      // Filter by selected status
      const statusArray = (environment as any).client_status || [];
      const selectedStatus = statusArray.find((status: any) => status.label === this.selectedStatusFilter);
      
      if (selectedStatus) {
        this.dataSource.data = (this.allClients || []).filter(client => 
          client.status_id === selectedStatus.id
        );
      } else {
        this.dataSource.data = this.allClients || [];
      }
    }
    
    // Update paginator
    if (this.paginator) {
      this.paginator.firstPage();
      this.pageIndex = 0;
    }
    
    console.log('Status filter applied:', this.selectedStatusFilter, 'Filtered data count:', this.dataSource.data.length);
  }

  /**
   * Handle paginator page events
   */
  onPageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.paginationService.setPageSize(event.pageSize);
    this.loadPage();
  }

  /**
   * Fetch one page from server using current paginator, search, and sort
   */
  private loadPage() {
    const pageNumber = this.pageIndex + 1; // API is 1-based
    const limit = this.pageSize;
    const searchTerm = this.searchValue || '';
    const sortBy = this.sortBy;
    const sortOrder = this.sortOrder;

    // Determine statusId based on selected filter
    let statusId: number | undefined = undefined;
    if (this.selectedStatusFilter !== 'Alle') {
      const statusArray = (environment as any).client_status || [];
      const selectedStatus = statusArray.find((status: any) => status.label === this.selectedStatusFilter);
      if (selectedStatus) {
        statusId = selectedStatus.id;
      }
    }

    this.isLoadingResults = true;
    this.kundeService.getKunden(pageNumber, limit, searchTerm, sortBy, sortOrder, statusId, this.selectedAlphabetFilter || undefined).subscribe(
      (response) => {
        this.allClients = response.data || [];
        this.dataSource.data = response.data || [];
        this.totalItems = response.total || 0;
        this.isLoadingResults = false;
        this.isRateLimitReached = false;

        // No need for client-side filtering since we filter at API level
      },
      (error) => {
        console.error('Error loading page:', error);
        this.isLoadingResults = false;
        this.isRateLimitReached = true;
      }
    );
  }

  /**
   * Get status description by status ID using environment array
   */
  getStatusDescription(statusId: number): string {
    const statusArray = (environment as any).client_status || [];
    const status = statusArray.find((s: any) => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  /**
   * Format date for display in German format
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  }

  /**
   * Format date for export (consistent format)
   */
  formatDateForExport(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '-';
    }
  }

  /**
   * Format kontakte for export - formats all kontakte as "Name (email); Name (email)"
   */
  formatKontakte(kunde: Kunde): string {
    // Check if kontakte are already loaded in the kunde object
    if ((kunde as any).kontakte && Array.isArray((kunde as any).kontakte)) {
      const kontakte = (kunde as any).kontakte;
      if (kontakte.length === 0) return '-';
      return kontakte
        .map((k: any) => {
          const name = k.name || k.unternehmensname || '-';
          const email = k.email || '';
          return email ? `${name} (${email})` : name;
        })
        .join('; ');
    }
    // If kontakte not loaded, return placeholder
    // Note: Loading kontakte for each kunde during export would be too slow
    return '-';
  }

  /**
   * Format anmerkungen JSON content to readable text
   */
  formatAnmerkungen(anmerkungen: string | undefined): string {
    if (!anmerkungen) return '-';
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(anmerkungen);
      if (Array.isArray(parsed)) {
        return parsed.join('; ');
      } else if (typeof parsed === 'object') {
        return Object.entries(parsed)
          .map(([key, value]) => `${key}: ${value}`)
          .join('; ');
      }
      return String(parsed);
    } catch (error) {
      // If not JSON, return as-is
      return anmerkungen;
    }
  }

  /**
   * Export table data to CSV
   */
  exportToCSV(): void {
    try {
      const exportColumns: ExportColumn<Kunde>[] = [
        { key: 'suchbegriff', label: 'Kunde' },
        { key: 'status_id', label: 'Status', transform: (val) => this.getStatusDescription(val) },
        { key: 'startdatum', label: 'Startdatum', transform: (val) => this.formatDateForExport(val) },
        { key: 'enddatum', label: 'Enddatum', transform: (val) => this.formatDateForExport(val) },
        { key: 'strasse', label: 'Straße' },
        { key: 'plz', label: 'PLZ' },
        { key: 'ort', label: 'Ort' },
        { key: 'land', label: 'Land' },
        { key: 'kontakte', label: 'Kontakte', transform: (val, row) => this.formatKontakte(row) },
        { key: 'anmerkungen', label: 'Anmerkungen', transform: (val) => this.formatAnmerkungen(val) }
      ];

      this.tableExportService.exportTable({
        data: this.dataSource.data,
        columns: exportColumns,
        filename: 'kunden',
        format: 'csv',
        includeHeaders: true
      });

      this._snackBar.open('Kunden erfolgreich als CSV exportiert', 'Schließen', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting kunden:', error);
      this._snackBar.open('Fehler beim Exportieren der Kunden', 'Schließen', { duration: 3000 });
    }
  }

  /**
   * Export table data to Excel
   */
  async exportToExcel(): Promise<void> {
    try {
      const exportColumns: ExportColumn<Kunde>[] = [
        { key: 'suchbegriff', label: 'Kunde' },
        { key: 'status_id', label: 'Status', transform: (val) => this.getStatusDescription(val) },
        { key: 'startdatum', label: 'Startdatum', transform: (val) => this.formatDateForExport(val) },
        { key: 'enddatum', label: 'Enddatum', transform: (val) => this.formatDateForExport(val) },
        { key: 'strasse', label: 'Straße' },
        { key: 'plz', label: 'PLZ' },
        { key: 'ort', label: 'Ort' },
        { key: 'land', label: 'Land' },
        { key: 'kontakte', label: 'Kontakte', transform: (val, row) => this.formatKontakte(row) },
        { key: 'anmerkungen', label: 'Anmerkungen', transform: (val) => this.formatAnmerkungen(val) }
      ];

      await this.tableExportService.exportTable({
        data: this.dataSource.data,
        columns: exportColumns,
        filename: 'kunden',
        format: 'xlsx',
        includeHeaders: true
      });

      this._snackBar.open('Kunden erfolgreich als Excel exportiert', 'Schließen', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting kunden:', error);
      this._snackBar.open('Fehler beim Exportieren der Kunden', 'Schließen', { duration: 3000 });
    }
  }
}
